/**
 * WerkstattZeiterfassung — Start/Stopp-Timer pro AW-Position
 *
 * Wird in der Werkstatt-Auftrag-Detailansicht eingebettet.
 * Mechaniker starten/stoppen Timer pro AW-Position; die verbrauchten
 * AW werden auf akten.aw_ist akkumuliert.
 *
 * 1 AW = 6 Min (Branchenstandard). Acceptance Criterion: 30 Min → 5 AW.
 * Issue #393.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle, Clock, Loader2, Plus, Square, Timer } from 'lucide-react';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Position {
  id: string;
  bezeichnung: string;
  aw_soll: number | null;
}

interface ZeitEintrag {
  id: string;
  position_id: string | null;
  mechaniker_id: string | null;
  start: string;
  ende: string | null;
}

interface OffenerTimer {
  id: string;
  auftrag_id: string;
  position_id: string | null;
  start: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SECONDS_PER_AW = 360;

function formatMmSs(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function totalSekundenFuerPosition(positionId: string | null, zeiten: ZeitEintrag[]): number {
  return zeiten
    .filter((z) => z.position_id === positionId && z.ende !== null)
    .reduce((acc, z) => {
      const dSec = (new Date(z.ende!).getTime() - new Date(z.start).getTime()) / 1000;
      return acc + Math.max(0, dSec);
    }, 0);
}

// ─── PositionZeile ────────────────────────────────────────────────────────────

function PositionZeile({
  auftragsId,
  position,
  zeiten,
  offenerTimer,
  userId,
  accessToken,
  onTimerGestartet,
  onTimerGestoppt,
}: {
  auftragsId: string;
  position: Position;
  zeiten: ZeitEintrag[];
  offenerTimer: OffenerTimer | null;
  userId: string | null;
  accessToken: string | null;
  onTimerGestartet: (zeitId: string, positionId: string) => void;
  onTimerGestoppt: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [laufendeSek, setLaufendeSek] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const laufenderTimerFuerDiesePosition =
    offenerTimer?.position_id === position.id ? offenerTimer : null;

  // Live-Timer updaten
  useEffect(() => {
    if (laufenderTimerFuerDiesePosition) {
      const update = () => {
        const elapsed =
          (Date.now() - new Date(laufenderTimerFuerDiesePosition.start).getTime()) / 1000;
        setLaufendeSek(Math.max(0, elapsed));
      };
      update();
      intervalRef.current = setInterval(update, 1000);
    } else {
      setLaufendeSek(0);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [laufenderTimerFuerDiesePosition]);

  async function handleStart() {
    if (!userId) {
      toast.error('Nicht angemeldet');
      return;
    }
    setLoading(true);
    const postHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) postHeaders['Authorization'] = `Bearer ${accessToken}`;
    try {
      const res = await fetch('/api/werkstatt-zeiterfassung', {
        method: 'POST',
        headers: postHeaders,
        body: JSON.stringify({
          action: 'start',
          auftragId: auftragsId,
          positionId: position.id,
          mechanikerId: userId,
        }),
      });
      const json = await res.json();
      if (!json.ok) {
        toast.error(json.error ?? 'Timer konnte nicht gestartet werden');
      } else {
        onTimerGestartet(json.zeitId, position.id);
        toast.success('Timer gestartet');
      }
    } catch {
      toast.error('Netzwerkfehler');
    } finally {
      setLoading(false);
    }
  }

  async function handleStopp() {
    if (!laufenderTimerFuerDiesePosition) return;
    setLoading(true);
    const postHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) postHeaders['Authorization'] = `Bearer ${accessToken}`;
    try {
      const res = await fetch('/api/werkstatt-zeiterfassung', {
        method: 'POST',
        headers: postHeaders,
        body: JSON.stringify({ action: 'stopp', zeitId: laufenderTimerFuerDiesePosition.id }),
      });
      const json = await res.json();
      if (!json.ok) {
        toast.error(json.error ?? 'Timer konnte nicht gestoppt werden');
      } else {
        onTimerGestoppt();
        toast.success(`Gestoppt — ${(json.aw_ist as number).toFixed(2)} AW gesamt`);
      }
    } catch {
      toast.error('Netzwerkfehler');
    } finally {
      setLoading(false);
    }
  }

  const erfassteSekundenGesamt = totalSekundenFuerPosition(position.id, zeiten);
  const erfassteAW = erfassteSekundenGesamt / SECONDS_PER_AW;
  const laeuft = laufenderTimerFuerDiesePosition !== null;
  const andererTimerLaeuft = offenerTimer !== null && !laeuft;

  return (
    <div className="flex items-center gap-3 py-3 border-b border-gray-100 last:border-0">
      {/* Position-Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{position.bezeichnung}</p>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
          {position.aw_soll !== null && (
            <span>Plan: {position.aw_soll} AW</span>
          )}
          {erfassteSekundenGesamt > 0 && (
            <span className="flex items-center gap-0.5">
              <CheckCircle className="w-3 h-3 text-green-500" />
              {erfassteAW.toFixed(2)} AW erfasst
            </span>
          )}
        </div>
      </div>

      {/* Laufender Timer */}
      {laeuft && (
        <div className="flex items-center gap-1 text-sm font-mono font-semibold text-[#B91C1C] min-w-[60px]">
          <Timer className="w-3.5 h-3.5 animate-pulse" />
          {formatMmSs(laufendeSek)}
        </div>
      )}

      {/* Start/Stopp Button — mind. 64×64 px für Tablet */}
      {laeuft ? (
        <button
          onClick={handleStopp}
          disabled={loading}
          aria-label="Timer stoppen"
          className="w-16 h-16 flex flex-col items-center justify-center gap-0.5 rounded-xl bg-[#B91C1C] text-white text-xs font-semibold hover:bg-[#991B1B] disabled:opacity-50 transition-colors shrink-0"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Square className="w-5 h-5" />
              Stopp
            </>
          )}
        </button>
      ) : (
        <button
          onClick={handleStart}
          disabled={loading || andererTimerLaeuft}
          aria-label="Timer starten"
          title={andererTimerLaeuft ? 'Anderen Timer zuerst stoppen' : undefined}
          className="w-16 h-16 flex flex-col items-center justify-center gap-0.5 rounded-xl border-2 border-gray-300 text-gray-600 text-xs font-semibold hover:border-[#B91C1C] hover:text-[#B91C1C] disabled:opacity-40 transition-colors shrink-0"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Clock className="w-5 h-5" />
              Start
            </>
          )}
        </button>
      )}
    </div>
  );
}

// ─── WerkstattZeiterfassung ───────────────────────────────────────────────────

interface Props {
  auftragsId: string;
  userId: string | null;
  accessToken: string | null;
}

export default function WerkstattZeiterfassung({ auftragsId, userId, accessToken }: Props) {
  const [positionen, setPositionen] = useState<Position[]>([]);
  const [zeiten, setZeiten] = useState<ZeitEintrag[]>([]);
  const [offenerTimer, setOffenerTimer] = useState<OffenerTimer | null>(null);
  const [loading, setLoading] = useState(true);
  const [neueBezeichnung, setNeueBezeichnung] = useState('');
  const [neueAwSoll, setNeueAwSoll] = useState('');
  const [addingPosition, setAddingPosition] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const authHeaders: Record<string, string> = accessToken
    ? { 'Authorization': `Bearer ${accessToken}` }
    : {};

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const headers: Record<string, string> = accessToken
        ? { 'Authorization': `Bearer ${accessToken}` }
        : {};
      const [posRes, zeitRes] = await Promise.all([
        fetch(`/api/werkstatt-zeiterfassung?action=positionen&auftragId=${auftragsId}`, { headers }),
        fetch(`/api/werkstatt-zeiterfassung?action=zeiten&auftragId=${auftragsId}`, { headers }),
      ]);
      const [posJson, zeitJson] = await Promise.all([posRes.json(), zeitRes.json()]);
      setPositionen(posJson.positionen ?? []);
      setZeiten(zeitJson.zeiten ?? []);

      // Offenen Timer für den angemeldeten Mechaniker laden
      if (userId) {
        const timerRes = await fetch(
          `/api/werkstatt-zeiterfassung?action=offenerTimer&mechanikerId=${userId}`,
          { headers },
        );
        const timerJson = await timerRes.json();
        setOffenerTimer(timerJson.offenerTimer ?? null);
      }
    } catch {
      toast.error('Zeiterfassung konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [auftragsId, userId, accessToken]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function handleAddPosition(e: React.FormEvent) {
    e.preventDefault();
    const bez = neueBezeichnung.trim();
    if (!bez) return;
    setAddingPosition(true);
    try {
      const res = await fetch('/api/werkstatt-zeiterfassung', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          action: 'addPosition',
          auftragId: auftragsId,
          bezeichnung: bez,
          awSoll: neueAwSoll ? parseFloat(neueAwSoll) : null,
        }),
      });
      const json = await res.json();
      if (!json.ok) {
        toast.error(json.error ?? 'Position konnte nicht angelegt werden');
      } else {
        setNeueBezeichnung('');
        setNeueAwSoll('');
        setShowAddForm(false);
        await reload();
      }
    } catch {
      toast.error('Netzwerkfehler');
    } finally {
      setAddingPosition(false);
    }
  }

  function handleTimerGestartet(zeitId: string, positionId: string) {
    setOffenerTimer({
      id: zeitId,
      auftrag_id: auftragsId,
      position_id: positionId,
      start: new Date().toISOString(),
    });
    reload();
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Zeiterfassung
        </h2>
        <button
          onClick={() => setShowAddForm((v) => !v)}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Position
        </button>
      </div>

      {/* Neue Position anlegen */}
      {showAddForm && (
        <form onSubmit={handleAddPosition} className="flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-40">
            <label className="block text-xs text-gray-500 mb-1">Bezeichnung</label>
            <input
              type="text"
              value={neueBezeichnung}
              onChange={(e) => setNeueBezeichnung(e.target.value)}
              placeholder="z.B. Bremsbeläge tauschen"
              required
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#B91C1C] focus:border-[#B91C1C]"
            />
          </div>
          <div className="w-24">
            <label className="block text-xs text-gray-500 mb-1">AW-Soll</label>
            <input
              type="number"
              step="0.5"
              min="0"
              value={neueAwSoll}
              onChange={(e) => setNeueAwSoll(e.target.value)}
              placeholder="z.B. 2"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#B91C1C] focus:border-[#B91C1C]"
            />
          </div>
          <button
            type="submit"
            disabled={addingPosition || !neueBezeichnung.trim()}
            className="px-3 py-2 bg-[#B91C1C] text-white text-sm font-medium rounded-lg hover:bg-[#991B1B] disabled:opacity-50 transition-colors"
          >
            {addingPosition ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Anlegen'}
          </button>
        </form>
      )}

      {/* Positions-Liste */}
      {positionen.length === 0 ? (
        <p className="text-sm text-gray-400 py-2">
          Noch keine AW-Positionen.{' '}
          <button
            onClick={() => setShowAddForm(true)}
            className="text-[#B91C1C] hover:underline"
          >
            Jetzt anlegen
          </button>
        </p>
      ) : (
        <div>
          {positionen.map((pos) => (
            <PositionZeile
              key={pos.id}
              auftragsId={auftragsId}
              position={pos}
              zeiten={zeiten}
              offenerTimer={offenerTimer}
              userId={userId}
              accessToken={accessToken}
              onTimerGestartet={handleTimerGestartet}
              onTimerGestoppt={reload}
            />
          ))}
        </div>
      )}
    </div>
  );
}
